import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardCheck, AlertTriangle, Check, Pencil, X } from 'lucide-react';
import { api } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { useAuth } from '../../lib/auth';
import { FotoProduto } from '../../components/FotoProduto';
import { formatDataSP } from '@fiodecobre/shared';
import { ModalAjustarEstoque } from '../produtos/ModalAjustarEstoque';

// CONFERENCIA DOS VENDIDOS ([DA-597]) — a tela do estoquista no tablet.
//
// A pergunta que ela responde, em uma linha: *"o que vendeu desde a ultima vez que eu contei?"*
//
// ─── O QUE MANDOU O DESENHO ────────────────────────────────────────────────────────────────────
// A conferencia e' do PRODUTO, medida pelo `Est. Atual` — palavra do Ruan: *"A contagem nao e por
// prateleira, e por Est. Atual, as prateleiras obrigatoriamente tem que ser coerente com este
// campo"*. Por isso o card e' de um PRODUTO, com as prateleiras dele dentro, e nao uma linha por
// prateleira.
//
// ─── ERGONOMIA, que aqui vale mais que estetica ───────────────────────────────────────────────
// Quem usa esta tela esta **de pe no galpao, com o tablet numa mao e o produto na outra**. Isso
// decide quase tudo:
//   - Dois botoes por card, largos, sem menu escondido: **Confere** (bateu) e **Corrigir** (nao
//     bateu). O gesto mais comum e' o mais barato.
//   - O numero do sistema vai GRANDE, porque e' o que ele compara com a prateleira.
//   - A PRATELEIRA vai em destaque: e' por ela que ele acha o produto. Sem ela o card obriga a
//     abrir a ficha pra saber onde ir.
//   - Nada de verde/vermelho (daltonismo do Ruan): confirmado usa o amarelo `#fde68a` com letra
//     preta, e atencao usa laranja `#f97316`.
//
// ⚠️ O card SO some depois que o servidor confirma. Sumir na hora do toque e' otimismo que mente
// quando a rede do galpao oscila — e ai ele acha que conferiu e nao conferiu.
type Lugar = {
  localizacao_id: number;
  codigo: string;
  principal: boolean;
  quantidade: string | null;
  contado_em: string | null;
  // So a vista Ronda manda os dois abaixo — e' o que o modal de ajuste da ficha precisa.
  descricao?: string | null;
  quantidade_minima?: string | null;
};

type MotivoRonda = 'zerado_vendia' | 'sem_prateleira' | 'endereco_antigo';

type ItemFila = {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  estoque_atual: string;
  conferido_em: string | null;
  ultima_saida: string | null;
  giro_90d: number;
  vendeu_desde_ontem: boolean;
  lugares: Lugar[];
  unidade?: string;
  motivo_ronda?: MotivoRonda;
  endereco_antigo?: string | null;
};

/**
 * O que a Ronda pede em cada card ([DA-868]). Frase de ACAO, nao de diagnostico: ele esta de pe no
 * galpao e precisa saber o que fazer, nao por que o sistema desconfia.
 */
function textoRonda(item: ItemFila): string {
  if (item.motivo_ronda === 'zerado_vendia') return 'Sistema diz zero, mas vendia. Procure em outros lugares.';
  if (item.motivo_ronda === 'sem_prateleira') return 'Tem estoque e nenhuma prateleira. Ache onde esta e cadastre.';
  if (item.motivo_ronda === 'endereco_antigo') return `O endereco antigo citava ${item.endereco_antigo ?? '—'}. Olhe la tambem.`;
  return '';
}

const CHAVE_VISTA = 'fdc.conferencia.vista';

/**
 * TRES VISTAS QUE SE EXCLUEM — nao dois checkboxes independentes ([DA-597]).
 *
 * A 1a versao tinha "Todos" e "Nunca Conferidos" combinaveis, dando quatro estados. O Ruan
 * tropecou no mesmo dia: a tela dizia *"307 nunca conferidos"* ao lado de um checkbox que, marcado
 * sozinho, mostrava **6**. Os dois numeros estavam certos — 307 e' sobre os 616 do catalogo, 6 e'
 * sobre os 80 do dia —, mas o mesmo rotulo aparecia com valores diferentes conforme o OUTRO
 * controle.
 *
 * ⚠️ **Numero exibido ao lado de um filtro promete ser o resultado daquele filtro.** Quando o
 * clique entrega outra coisa, a culpa e' do desenho. Com tres vistas exclusivas, o que a tela
 * mostra e' sempre o que o botao aceso diz.
 */
const VISTAS = [
  // "Ultimo dia de vendas", nao "Do dia": o rotulo tem que dizer que a lista NAO muda durante o
  // expediente. Correcao dele — *"dessa forma os registros nao vao mudar"*.
  { id: 'dia' as const, rotulo: 'Ultimo dia de vendas' },
  { id: 'todos' as const, rotulo: 'Todos' },
  { id: 'nunca' as const, rotulo: 'Nunca conferidos' },
  // A unica vista que NAO nasce de venda ([DA-868]): o zerado nao vende, entao as outras tres
  // nunca o mostram — foi assim que a torneira 0557 ficou 15 dias sumida.
  { id: 'ronda' as const, rotulo: 'Ronda' },
];
type Vista = (typeof VISTAS)[number]['id'];

/** Motivos que cobrem o que de fato acontece na loja. O campo livre fica pro resto. */
const MOTIVOS = ['Quebrado / avariado', 'Achei em outra prateleira', 'Erro de lancamento', 'Sobrou na contagem'];

const fmt = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  // Quantidade, nao dinheiro: mostra ate 3 casas mas nao enche de zero o numero redondo.
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
};

/** '29.000' -> '29' pra CAMPO DE DIGITACAO (sem separador de milhar, que aqui enganaria). */
const semZeroAtoa = (v: string | null | undefined) => {
  if (v === null || v === undefined || v === '') return '0';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : String(v);
};

/**
 * Quando o produto saiu pela ultima vez, dito como gente fala.
 *
 * ⚠️ A 1a versao imprimia "vendeu ontem" sempre que a saida fosse de ontem PRA CA — entao um
 * produto vendido hoje as 16:58 aparecia como "vendeu ontem". Texto que afirma a coisa errada nao
 * degrada: fica na cara de quem usa, e nenhum dos portoes le portugues.
 */
function quandoVendeu(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dia = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  const hoje = new Date();
  const ontem = new Date(hoje.getTime() - 86400000);
  if (dia(d) === dia(hoje)) return 'vendeu hoje';
  if (dia(d) === dia(ontem)) return 'vendeu ontem';
  return `vendeu ${formatDataSP(iso)}`;
}

export default function Conferencia() {
  const dialog = useDialog();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';

  const [itens, setItens] = useState<ItemFila[]>([]);
  // Quantos ha NA FILA, que nao e' o mesmo que quantos couberam no limite da consulta.
  const [total, setTotal] = useState(0);
  // Nunca conferidos NA FILA INTEIRA — nao na fatia carregada, que dizia "300" com 307 no total.
  const [totalNunca, setTotalNunca] = useState(0);
  // De que dia e' a lista (o ultimo dia de vendas ja fechado). Null = nao ha dia elegivel ainda.
  const [diaDaLista, setDiaDaLista] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState<number | null>(null);
  const [gravando, setGravando] = useState<number | null>(null);
  // Quantos de cada motivo a Ronda tem NA FILA INTEIRA (nao na fatia carregada).
  const [porMotivo, setPorMotivo] = useState<Record<MotivoRonda, number> | null>(null);

  // O `dialog` vai por ref, NUNCA nas deps: o provider devolve objeto novo a cada render e a lista
  // recarregaria a cada confirmacao aberta/fechada, com a resposta velha repintando a nova
  // ([DA-505] p.11).
  const dialogRef = useRef(dialog);
  useEffect(() => { dialogRef.current = dialog; }, [dialog]);

  // Espelho do total pro listener de foco (que e' registrado uma vez e nao enxerga o state novo).
  // Copiar por efeito cobre TODOS os caminhos que mexem no numero — inclusive o decremento de quem
  // sai conferido.
  const totalEspelho = useRef(0);

  // Guarda contra corrida: resposta antiga que chega depois nao pode repintar a lista.
  const reqId = useRef(0);


  const carregar = useCallback(async (vista: Vista, silencioso = false) => {
    const req = ++reqId.current;
    // ⚠️ Recarga de FUNDO nao acende o loading ([DA-298]): trocar o conteudo por um spinner
    // DESMONTA os cards e leva junto o estado deles. O dado se troca por baixo.
    if (!silencioso) setCarregando(true);
    try {
      const r = await api.get<{
        itens: ItemFila[]; total: number; total_nunca_conferidos: number;
        ponto_zero: string | null; dia_da_lista: string | null;
        por_motivo?: Record<MotivoRonda, number>;
      }>(`/api/estoque/conferencia?limite=300&vista=${vista}`);
      if (req !== reqId.current) return;
      setPorMotivo(r.por_motivo ?? null);
      const novoTotal = r.total ?? r.itens.length;
      // Entrou coisa na fila enquanto a tela estava aberta? A lista mudar CALADA faz quem terminou
      // os 80 concluir que zerou o dia. Entram 71 a 104 produtos por dia — nao e' hipotese.
      if (silencioso && novoTotal > totalEspelho.current) {
        const n = novoTotal - totalEspelho.current;
        dialogRef.current.toast(n === 1 ? '1 produto entrou na fila' : `${n} produtos entraram na fila`);
      }
      setItens(r.itens);
      setTotal(novoTotal);
      setTotalNunca(r.total_nunca_conferidos ?? 0);
      setDiaDaLista(r.dia_da_lista ?? null);
      setErro(null);
    } catch (e: any) {
      if (req !== reqId.current) return;
      setErro(e?.message ?? 'Nao foi possivel carregar a fila.');
    } finally {
      if (req === reqId.current) setCarregando(false);
    }
  }, []);

  // A escolha do "Todos" PERSISTE por aparelho (convencao da casa pra filtro de tela): quem abriu o
  // backlog uma vez costuma querer continuar nele.
  const [vista, setVista] = useState<Vista>(() => {
    const salvo = localStorage.getItem(CHAVE_VISTA);
    return VISTAS.some((v) => v.id === salvo) ? (salvo as Vista) : 'dia';
  });
  useEffect(() => { localStorage.setItem(CHAVE_VISTA, vista); }, [vista]);

  useEffect(() => { void carregar(vista); }, [carregar, vista]);

  // ── RECARGA AO VOLTAR PRO FOCO ───────────────────────────────────────────────────────────────
  //
  // O botao Atualizar sozinho e' rede fraca: nada na tela diz que a lista envelheceu, entao quem
  // termina os 80 da manha conclui que zerou o dia — sem saber que entraram mais 15 a tarde.
  // O gesto natural do estoquista ja resolve: ele larga o tablet pra contar e volta. Aqui a volta
  // vira recarga.
  //
  // ⚠️ DUAS GUARDAS, e a primeira e' a que importa:
  //   1. **Correcao aberta NAO e' interrompida.** Recarregar por cima apagaria o numero que ele
  //      acabou de digitar — o defeito da [DA-298] com outra roupa. A recarga espera ele fechar.
  //   2. Recarga silenciosa (sem spinner), senao os cards desmontam a cada ida e volta.
  //
  // Os valores vivos vao por REF: o listener e' registrado uma vez e leria o estado congelado da
  // montagem se dependesse do closure.
  const editandoRef = useRef<number | null>(null);
  useEffect(() => { editandoRef.current = editando; }, [editando]);
  const vistaRef = useRef(vista);
  useEffect(() => { vistaRef.current = vista; }, [vista]);
  useEffect(() => { totalEspelho.current = total; }, [total]);

  useEffect(() => {
    const aoVoltar = () => {
      if (document.visibilityState !== 'visible') return;
      if (editandoRef.current !== null) return;
      void carregar(vistaRef.current, true);
    };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);
    return () => {
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
    };
  }, [carregar]);


  /** "Contei e bateu" — so carimba a data. */
  async function confere(item: ItemFila) {
    setGravando(item.produto_id);
    try {
      await api.post(`/api/estoque/conferencia/${item.produto_id}/conferir`, {});
      reqId.current++; // a lista que estiver em voo nao pode ressuscitar este card
      setItens((prev) => prev.filter((i) => i.produto_id !== item.produto_id));
      setTotal((t) => Math.max(0, t - 1));
      dialogRef.current.toast(`${item.codigo_interno ?? ''} conferido`);
    } catch (e: any) {
      await dialogRef.current.alerta(e?.message ?? 'Nao foi possivel marcar como conferido.', { titulo: 'Nao gravou', tipo: 'aviso' });
    } finally {
      setGravando(null);
    }
  }

  return (
    <div className="pagina">
      {/* O `.pagina-titulo` nao e' estetica: sem ele o titulo cai pra 14px peso 400 (o preflight do
          Tailwind zera h1..h6) e a aba do navegador perde o nome. */}
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <ClipboardCheck size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} />
          Conferencia dos Vendidos
        </h2>
        {/* `flexWrap`: com a 4a vista (Ronda, [DA-868]) os botoes passaram da largura do tablet
            (1200px) e o "Ronda" saia cortado com rolagem horizontal — medido no ensaio. */}
        <div className="pagina-acoes" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/* Fica ANTES do contador de proposito: e' ele que muda o numero ao lado, e controle que
              altera um valor mora colado nele.
              ⚠️ O aceso NAO usa o `#fde68a` da casa: nesta tela aquele amarelo ja significa "Confere"
              (a acao principal) e "motivo escolhido" — um terceiro sentido pra mesma cor viraria
              ambiguidade. Aqui e' estado de navegacao, entao veste o azul do tema. */}
          <div style={{ display: 'inline-flex', border: '1px solid var(--panel-border)', borderRadius: 6, overflow: 'hidden' }}>
            {VISTAS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setVista(v.id)}
                aria-pressed={vista === v.id}
                style={{
                  padding: '8px 14px', fontSize: 14, cursor: 'pointer', border: 'none',
                  whiteSpace: 'nowrap',
                  background: vista === v.id ? 'var(--primary)' : 'transparent',
                  color: vista === v.id ? '#fff' : 'inherit',
                  fontWeight: vista === v.id ? 700 : 400,
                }}
              >
                {v.rotulo}
              </button>
            ))}
          </div>
          {!carregando && (
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {/* Quando a fila e' maior que o que coube, a tela DIZ — teto calado le como "acabou". */}
              {itens.length < total ? `${itens.length} de ${total} pra conferir` : `${total} pra conferir`}
              {/* Com o filtro ligado TODOS sao nunca conferidos — repetir o numero ao lado nao diria
                  nada novo, e texto de UI so se paga se contar o que a tela nao conta sozinha. */}
              {vista !== 'nunca' && vista !== 'ronda' && totalNunca > 0 && (
                // Azul escuro, nao laranja: medido na tela, o laranja sobre o fundo do cabecalho
                // dava contraste 1.34 e o Ruan nao conseguia ler ([DA-597]). O laranja e' calibrado
                // pra faixa de fundo SOLIDO — aqui o fundo e' azul claro e ele some.
                <span style={{ color: 'var(--num-destaque)', fontWeight: 700, marginLeft: 8 }}>
                  · {totalNunca} nunca conferidos
                </span>
              )}
            </span>
          )}
          <button type="button" className="btn-secundario" onClick={() => void carregar(vista)} disabled={carregando}>
            Atualizar
          </button>
        </div>
      </div>

      {erro && (
        <div style={{ background: '#f97316', color: '#000', padding: 12, borderRadius: 6, marginBottom: 12, fontWeight: 600 }}>
          {erro}
        </div>
      )}

      {ehAdmin && <FaixaAjustes />}

      {carregando && <div className="muted" style={{ padding: 16 }}>Carregando a fila...</div>}

      {/* Vazio nao e' so ausencia: diz o que aconteceu e o que esperar. */}
      {!carregando && itens.length === 0 && !erro && (
        <div style={{
          border: '1px solid var(--panel-border)', borderRadius: 6, padding: 24,
          background: 'var(--panel-bg)', textAlign: 'center',
        }}>
          {/* Vazio nao e' so ausencia: diz POR QUE esta vazio e o que esperar. Sem isso, a tela que
              ainda nao tem dia elegivel le exatamente como a que ja foi toda conferida. */}
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>
            {vista === 'ronda' ? 'Ronda limpa' : vista === 'dia' && !diaDaLista ? 'Nada pra conferir ainda' : 'Fila limpa'}
          </div>
          <div className="muted" style={{ fontSize: 14 }}>
            {vista === 'ronda'
              ? 'Nenhum produto zerado que vendia, sem prateleira ou com endereco antigo pendente.'
              : vista === 'dia' && !diaDaLista
                ? 'A conferencia comeca no primeiro dia de vendas fechado a partir do inicio do programa. Amanha esta lista traz o que vendeu hoje.'
                : 'Tudo que vendeu ja foi conferido depois da ultima venda. Novos produtos aparecem aqui conforme forem vendidos.'}
          </div>
        </div>
      )}

      {/* Uma frase por vista — cada uma descreve exatamente o que esta na tela. Frase fixa faria a
          tela afirmar um recorte que o botao ao lado acabou de trocar. */}
      {!carregando && (
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          {vista === 'ronda'
            ? `Produtos que a lista de vendidos nunca mostra: ${porMotivo?.zerado_vendia ?? 0} zerados que vendiam · ${porMotivo?.sem_prateleira ?? 0} sem prateleira · ${porMotivo?.endereco_antigo ?? 0} com endereco antigo. Os zerados primeiro — e' venda se perdendo agora.`
            : vista === 'nunca'
            ? 'Produtos que NUNCA foram conferidos — nem uma vez. Inclui os que nao vendem, que nao aparecem na lista do dia.'
            : vista === 'todos'
              ? 'Tudo que esta sem conferencia, sem recorte de data.'
              : diaDaLista
                // A data faz parte da afirmacao: sem ela, ninguem sabe se esta conferindo o
                // movimento de ontem ou o da sexta passada.
                ? `Vendidos em ${formatDataSP(`${diaDaLista}T12:00:00-03:00`)} — o ultimo dia de vendas fechado. A lista nao muda enquanto a loja vende hoje.`
                : 'Ainda nao ha um dia de vendas fechado pra conferir.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 860 }}>
        {itens.map((item) => (
          <Card
            key={item.produto_id}
            item={item}
            aberto={editando === item.produto_id}
            gravando={gravando === item.produto_id}
            onAbrir={() => setEditando(item.produto_id)}
            onFechar={() => setEditando(null)}
            onConfere={() => void confere(item)}
            onCorrigido={() => {
              reqId.current++;
              setItens((prev) => prev.filter((i) => i.produto_id !== item.produto_id));
              setTotal((t) => Math.max(0, t - 1));
              setEditando(null);
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── O CARD ────────────────────────────────────────────────────────────────────────────────────
function Card({ item, aberto, gravando, onAbrir, onFechar, onConfere, onCorrigido }: {
  item: ItemFila;
  aberto: boolean;
  gravando: boolean;
  onAbrir: () => void;
  onFechar: () => void;
  onConfere: () => void;
  onCorrigido: () => void;
}) {
  const nunca = !item.conferido_em;
  const lugaresTexto = item.lugares.map((l) => l.codigo).join(' · ') || 'sem prateleira';

  return (
    <div style={{
      border: '1px solid var(--panel-border)',
      // Laranja = atencao (nunca conferido), nunca vermelho: vermelho e' erro real, e o Ruan nao
      // separa verde de vermelho.
      borderLeft: nunca ? '5px solid #f97316' : '1px solid var(--panel-border)',
      borderRadius: 6,
      padding: 12,
      background: 'var(--panel-bg)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {/* A peca da casa, nao um `<img>` copiado da tela vizinha: ela traz `contain` + fundo
            branco em par, sem os quais produto comprido (cano, perfil, barra) perde o rotulo que o
            identifica ([DA-526]). */}
        {item.foto_url
          ? <FotoProduto url={item.foto_url} tam={56} />
          : <div className="produto-thumb-vazio" style={{ width: 56, height: 56, flex: '0 0 auto' }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="text-gray-500 font-bold no-underline" style={{ fontSize: 12 }}>{item.codigo_interno}</div>
          <div style={{ fontWeight: 600, fontSize: 16, lineHeight: 1.2 }}>{item.descricao}</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            {/* A prateleira e' como ele ACHA o produto — vai em destaque, nao em texto de apoio. */}
            <strong>{lugaresTexto}</strong>
            <span className="muted">
              {' · '}
              {nunca ? 'nunca conferido' : `conferido ${formatDataSP(item.conferido_em)}`}
              {quandoVendeu(item.ultima_saida) ? ` · ${quandoVendeu(item.ultima_saida)}` : ''}
            </span>
          </div>
          {item.motivo_ronda && (
            <div style={{ fontSize: 13, marginTop: 4, fontWeight: 700, color: 'var(--num-destaque)' }}>
              {textoRonda(item)}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div className="muted" style={{ fontSize: 11 }}>Sistema diz</div>
          <div className="cel-num" style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1 }}>
            {fmt(item.estoque_atual)}
          </div>
        </div>
      </div>

      {/* Na Ronda o Corrigir abre o ajuste da FICHA ([DA-602]), nao a correcao em linha: e' ele que
          CRIA prateleira — e achar o lugar que o sistema nao conhece e' o trabalho inteiro da Ronda.
          Mesma rota por baixo (`/conferir`), mesmo motivo obrigatorio, mesmo aviso ao admin. */}
      {aberto && item.motivo_ronda && (
        <ModalAjustarEstoque
          produtoId={item.produto_id}
          codigo={item.codigo_interno ?? ''}
          descricao={item.descricao}
          unidade={item.unidade ?? 'UN'}
          estoqueAtual={item.estoque_atual}
          locais={item.lugares.map((l) => ({
            localizacao_id: l.localizacao_id,
            principal: l.principal,
            quantidade: l.quantidade,
            quantidade_minima: l.quantidade_minima ?? null,
            codigo: l.codigo,
            descricao: l.descricao ?? null,
          }))}
          onFechar={onFechar}
          onSalvo={() => onCorrigido()}
        />
      )}
      {!aberto || item.motivo_ronda ? (
        <div style={{ display: 'flex', gap: 8 }}>
          {/* Sem prateleira, "confere" nao resolve nada: o produto continuaria sem onde procurar. */}
          {item.motivo_ronda !== 'sem_prateleira' && (
          <button
            type="button"
            onClick={onConfere}
            disabled={gravando}
            style={{
              flex: 2, padding: '14px 0', fontSize: 16, fontWeight: 700, borderRadius: 6,
              border: 'none', cursor: 'pointer',
              background: '#fde68a', color: '#000',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              whiteSpace: 'nowrap', opacity: gravando ? 0.5 : 1,
            }}
          >
            <Check size={18} /> {gravando ? 'Gravando...' : item.motivo_ronda === 'zerado_vendia' ? 'Zerado mesmo' : 'Confere'}
          </button>
          )}
          <button
            type="button"
            className="btn-secundario"
            onClick={onAbrir}
            disabled={gravando}
            style={{ flex: 1, padding: '14px 0', fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap' }}
          >
            <Pencil size={16} /> Corrigir
          </button>
        </div>
      ) : (
        <Correcao item={item} onCancelar={onFechar} onPronto={onCorrigido} />
      )}
    </div>
  );
}

// ── A CORRECAO ────────────────────────────────────────────────────────────────────────────────
//
// Aparece so quando ele diz que nao bateu. Tres coisas, nesta ordem: quanto tem de verdade, de qual
// prateleira saiu a diferenca (so quando ha mais de uma), e por que.
function Correcao({ item, onCancelar, onPronto }: {
  item: ItemFila;
  onCancelar: () => void;
  onPronto: () => void;
}) {
  const dialog = useDialog();
  const multi = item.lugares.length > 1;

  const [total, setTotal] = useState('');
  const [motivo, setMotivo] = useState('');
  // ⚠️ O numeric do Postgres chega como '29.000', e num campo de digitacao isso LE COMO 29 MIL —
  // o separador decimal em pt-BR e' a virgula, entao o ponto vira milhar na cabeca de quem olha.
  // Visto na tela publicada, nao no codigo: `29.000` e `89.000` lado a lado num produto que tem 118.
  const [porLugar, setPorLugar] = useState<Record<number, string>>(
    () => Object.fromEntries(item.lugares.map((l) => [l.localizacao_id, semZeroAtoa(l.quantidade)])),
  );
  const [enviando, setEnviando] = useState(false);
  const campoTotal = useRef<HTMLInputElement>(null);

  useEffect(() => { campoTotal.current?.focus(); }, []);

  const totalNum = Number(total.replace(',', '.'));
  const somaLugares = useMemo(
    () => item.lugares.reduce((s, l) => s + (Number(String(porLugar[l.localizacao_id] ?? '0').replace(',', '.')) || 0), 0),
    [porLugar, item.lugares],
  );

  // Com UM lugar so nao ha o que repartir: a prateleira recebe o total e pronto.
  const lugaresPayload = multi
    ? item.lugares.map((l) => ({
        localizacao_id: l.localizacao_id,
        quantidade: Number(String(porLugar[l.localizacao_id] ?? '0').replace(',', '.')) || 0,
      }))
    : item.lugares.map((l) => ({ localizacao_id: l.localizacao_id, quantidade: totalNum }));

  const fecha = !multi || Math.abs(somaLugares - totalNum) < 0.001;
  const valido = total.trim() !== '' && Number.isFinite(totalNum) && motivo.trim() !== '' && fecha;

  // ⚠️ Botao desabilitado DIZ por que — botao apagado e mudo e' beco sem saida no balcao.
  const falta = !total.trim() ? 'Digite quanto voce contou.'
    : !Number.isFinite(totalNum) ? 'O total precisa ser um numero.'
    : !fecha ? `As prateleiras somam ${fmt(somaLugares)} e o total e ${fmt(totalNum)} — acerte um dos dois.`
    : !motivo.trim() ? 'Diga por que o numero mudou.'
    : null;

  async function enviar() {
    setEnviando(true);
    try {
      await api.post(`/api/estoque/conferencia/${item.produto_id}/conferir`, {
        estoque_atual: totalNum,
        motivo: motivo.trim(),
        lugares: lugaresPayload,
      });
      dialog.toast(`${item.codigo_interno ?? ''} corrigido para ${fmt(totalNum)}`);
      onPronto();
    } catch (e: any) {
      await dialog.alerta(e?.message ?? 'Nao foi possivel corrigir.', { titulo: 'Nao gravou', tipo: 'aviso' });
    } finally {
      setEnviando(false);
    }
  }

  const diferenca = Number.isFinite(totalNum) && total.trim() !== ''
    ? Number((totalNum - Number(item.estoque_atual)).toFixed(3))
    : null;

  return (
    <div style={{ borderTop: '1px solid var(--page-bg)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label className="muted" style={{ fontSize: 11, display: 'block', marginBottom: 2 }}>Contei</label>
          <input
            ref={campoTotal}
            value={total}
            onChange={(e) => setTotal(e.target.value)}
            inputMode="decimal"
            placeholder="0"
            className="cel-num"
            style={{ fontSize: 22, fontWeight: 700, width: 140, padding: '10px 12px', textAlign: 'right' }}
          />
        </div>
        {diferenca !== null && diferenca !== 0 && (
          <div style={{
            background: '#f97316', color: '#000', padding: '6px 10px', borderRadius: 6,
            fontWeight: 700, fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <AlertTriangle size={16} />
            {diferenca > 0 ? `sobrando ${fmt(diferenca)}` : `faltando ${fmt(Math.abs(diferenca))}`}
          </div>
        )}
      </div>

      {/* So com 2+ prateleiras: alguem tem que dizer de qual saiu a diferenca. O sistema escolhendo
          sozinho acerta as vezes e cria erro silencioso no resto. */}
      {multi && (
        <div>
          <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
            Quanto ficou em cada prateleira (a soma tem que dar {total.trim() ? fmt(totalNum) : 'o total'})
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {item.lugares.map((l) => (
              <div key={l.localizacao_id}>
                <label className="muted" style={{ fontSize: 11, display: 'block' }}>
                  {l.codigo}{l.principal ? ' ★' : ''}
                </label>
                <input
                  value={porLugar[l.localizacao_id] ?? ''}
                  onChange={(e) => setPorLugar((p) => ({ ...p, [l.localizacao_id]: e.target.value }))}
                  inputMode="decimal"
                  className="cel-num"
                  style={{ width: 100, padding: '8px 10px', textAlign: 'right', fontSize: 15 }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>Por que mudou?</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {MOTIVOS.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMotivo(m)}
              style={{
                padding: '8px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
                border: '1px solid var(--panel-border)',
                background: motivo === m ? '#fde68a' : 'transparent',
                color: motivo === m ? '#000' : 'inherit',
                fontWeight: motivo === m ? 700 : 400,
              }}
            >
              {m}
            </button>
          ))}
        </div>
        <input
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="ou escreva o motivo"
          className="no-upper"
          style={{ width: '100%', padding: '10px 12px', fontSize: 14 }}
        />
      </div>

      {falta && <div style={{ fontSize: 13, color: '#f97316', fontWeight: 600 }}>{falta}</div>}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => void enviar()}
          disabled={!valido || enviando}
          style={{
            flex: 2, padding: '14px 0', fontSize: 16, fontWeight: 700, borderRadius: 6,
            border: 'none', cursor: valido && !enviando ? 'pointer' : 'not-allowed',
            background: valido ? '#fde68a' : 'var(--page-bg)',
            color: valido ? '#000' : 'var(--text-muted, #94a3b8)',
            whiteSpace: 'nowrap',
          }}
        >
          {enviando ? 'Gravando...' : 'Corrigir e conferir'}
        </button>
        <button
          type="button"
          className="btn-secundario"
          onClick={onCancelar}
          disabled={enviando}
          style={{ flex: 1, padding: '14px 0', fontSize: 15, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap' }}
        >
          <X size={16} /> Cancelar
        </button>
      </div>
    </div>
  );
}

// ── A FAIXA DE AJUSTES (so admin/gerente) ─────────────────────────────────────────────────────
//
// *"Eu preciso saber quando houver ajuste manual."* Ela vive DENTRO da tela e nao no topo do app de
// proposito nesta primeira volta: aqui ela ja aparece pra quem abre a Conferencia. A faixa global
// entra em seguida, pelo `useAlertaFaixa` da [DA-426].
//
// ⚠️ Ela PRECISA apagar (o "Dar por visto"), senao acende no 1o ajuste e nunca mais sai — e alarme
// que cobra o que ja foi resolvido custa a atencao duas vezes ([DA-410]).
function FaixaAjustes() {
  const dialog = useDialog();
  const [dados, setDados] = useState<{ pendentes: number; itens: any[] } | null>(null);
  const [abertoDetalhe, setAbertoDetalhe] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get<{ pendentes: number; itens: any[] }>('/api/estoque/conferencia/ajustes?pendentes=1');
      setDados(r);
    } catch { /* faixa e' acessorio: falhar aqui nao pode derrubar a fila */ }
  }, []);

  useEffect(() => { void carregar(); }, [carregar]);

  if (!dados || dados.pendentes === 0) return null;

  async function darPorVisto() {
    const ok = await dialog.confirmar(
      `Marcar ${dados!.pendentes} ajuste(s) como vistos? A faixa some ate o proximo ajuste.`,
      { titulo: 'Dar por visto' },
    );
    if (!ok) return;
    await api.post('/api/estoque/conferencia/ajustes/reconhecer', {});
    await carregar();
  }

  return (
    <div style={{ background: '#f97316', color: '#000', borderRadius: 6, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <AlertTriangle size={18} />
        <strong style={{ fontSize: 15 }}>
          {dados.pendentes === 1 ? '1 ajuste manual de estoque' : `${dados.pendentes} ajustes manuais de estoque`}
        </strong>
        <button
          type="button"
          onClick={() => setAbertoDetalhe((v) => !v)}
          style={{ background: '#000', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          {abertoDetalhe ? 'Ocultar' : 'Ver quais'}
        </button>
        <button
          type="button"
          onClick={() => void darPorVisto()}
          style={{ background: 'transparent', color: '#000', border: '1px solid #000', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >
          Dar por visto
        </button>
      </div>

      {abertoDetalhe && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {dados.itens.map((a) => (
            <div key={a.id} style={{ background: 'rgba(0,0,0,0.08)', borderRadius: 4, padding: '8px 10px', fontSize: 13 }}>
              <strong>{a.codigo_interno}</strong> {String(a.descricao).slice(0, 40)}
              {' — '}
              <span className="cel-num">{fmt(a.estoque_antes)} → {fmt(a.estoque_depois)}</span>
              {' · '}<em>{a.motivo}</em>
              {' · '}{a.usuario_nome} em {formatDataSP(a.criado_em)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
